import { guardianCloud } from './cloud-client.js';

// Keep the phone Add button in the main application bundle. The preview is an
// iframe and must not depend on a second script starting independently.
window.__guardianBootstrapReady = true;
// Replace the visual control once after startup. This releases it from stale
// document-level gesture handlers left by earlier mobile builds.
const staleAdd = document.querySelector('.add');
if (staleAdd) {
  const freshAdd = staleAdd.cloneNode(true);
  staleAdd.replaceWith(freshAdd);
}

// Keep the custom-photo action at the document level. The account dialog is
// rebuilt by the feature layer, so attaching to a particular input instance
// left the visible file picker without a live handler on some reloads.
document.addEventListener('change', async event => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('.auth-dialog .profile-photo')) return;
  event.stopImmediatePropagation();
  const file = input.files?.[0]; if (!file) return;
  const dialog = input.closest('.auth-dialog');
  const custom = dialog?.querySelector('input[name="profileIconChoice"][value="custom"]');
  if (custom) custom.checked = true;
  input.disabled = true;
  try {
    await guardianCloud.saveProfileAvatar(file);
    // Only reload after storage and the profile record have both succeeded.
    location.reload();
  } catch (error) {
    input.disabled = false;
    const toast = document.querySelector('.toast');
    if (toast) { toast.textContent = error?.message || 'Profile picture upload failed.'; toast.classList.add('show'); }
  }
}, true);
document.addEventListener('click', event => {
  if (!event.target.closest('.add')) return;
  const backdrop = document.querySelector('.editor-backdrop');
  const editor = backdrop?.querySelector('.editor');
  if (!backdrop || !editor) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.CleanApp?.openEditor?.('Event');
  if (!backdrop.classList.contains('show')) {
    backdrop.classList.add('show');
    editor.querySelector('.editor-kicker')?.replaceChildren('Create event');
    const heading = editor.querySelector('h2');
    if (heading) heading.textContent = 'New Event';
    const title = editor.querySelector('[name="title"]');
    if (title) { title.value = ''; title.focus(); }
  }
}, true);

// The editor is shared by every page.  Its original close handler belonged to
// the prototype shell, so a later rebuilt drawer could keep the visible × but
// lose its listener.  Delegate it at the document level so closing is reliable
// on desktop and mobile regardless of which feature opened the editor.
document.addEventListener('click', event => {
  const close = event.target.closest('.editor-close');
  if (!close) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  window.CleanApp?.closeEditor?.();
  document.querySelector('.editor-backdrop')?.classList.remove('show');
  if (location.hash === '#new-event') history.replaceState(null, '', location.pathname + location.search);
}, true);

const SESSION_KEY = 'clean-auth-session-v1';
const PREFS_KEY = 'clean-planner-prefs-v1';
const SNAPSHOT_KEY = 'clean-planner-local-v3';
const CLOUD_PREFS_HYDRATED = 'calendar-guardian-cloud-preferences-hydrated';
const CLOUD_SNAPSHOT_HYDRATED = 'calendar-guardian-cloud-snapshot-hydrated';
const CLOUD_DEFAULTS = { theme: 'clean', scale: 100, layout: 'columns', plannerMargin: true, workShading: true, mondayStart: true, morningBrief: true, routinePrompts: true, deadlinePrompts: false, roomyCards: false, expandedNav: true, reduceMotion: false, highContrast: false, largeControls: false };
const waitFor = selector => new Promise(resolve => {
  const found = document.querySelector(selector);
  if (found) return resolve(found);
  const timer = setInterval(() => {
    const element = document.querySelector(selector);
    if (element) { clearInterval(timer); resolve(element); }
  }, 50);
});

const setMessage = (form, message, error = false) => {
  let node = form.querySelector('.cloud-message');
  if (!node) {
    node = document.createElement('p');
    node.className = 'cloud-message';
    form.querySelector('.primary-action').before(node);
  }
  node.textContent = message;
  node.dataset.error = String(error);
};

const sessionRecord = user => ({
  id: user.id,
  email: user.email,
  name: user.user_metadata?.display_name || user.email?.split('@')[0] || 'Guardian user',
  iconChoice: user.user_metadata?.icon_choice || 'female',
  cloud: true,
});

const clearInvalidAccountCache = () => {
  for (const store of [localStorage, sessionStorage]) {
    for (let index = store.length - 1; index >= 0; index -= 1) {
      const key = store.key(index) || '';
      if (key.startsWith('clean-') || key.startsWith('calendar-guardian-') || key.startsWith('sb-')) store.removeItem(key);
    }
  }
};

async function initializeCloudAuth() {
  document.body.classList.toggle('cloud-configured', guardianCloud.configured);
  const form = await waitFor('.auth-form');
  const security = form.querySelector('.connection-security');
  if (!guardianCloud.configured) {
    security.textContent = 'Demo mode keeps sample changes on this PC. Connect the free secure account service before creating a real account.';
    form.hidden = true;
    form.closest('.auth-signed-out')?.querySelector('.auth-tabs')?.setAttribute('hidden', '');
    return;
  }

  if (form.elements.staySignedIn) form.elements.staySignedIn.checked = guardianCloud.staysSignedIn();

  // Supabase normally consumes the OAuth callback automatically, but mobile
  // browsers can resume the app before that listener runs.  Finish a PKCE
  // callback explicitly so "Continue with Google" cannot land back signed out.
  try { await guardianCloud.finishOAuthRedirect(); }
  catch (error) { console.warn('Unable to finish Google sign-in callback', error); }
  const cachedSession = await guardianCloud.session();
  const session = await guardianCloud.verifiedSession();
  if (cachedSession && !session) {
    clearInvalidAccountCache();
    location.reload();
    return;
  }
  if (session?.user) {
    let profile = {};
    try { profile = await guardianCloud.loadProfile(); } catch (error) { console.warn('Unable to load cloud profile', error); }
    const authenticatedProfile = { ...sessionRecord(session.user), ...profile };
    localStorage.setItem(SESSION_KEY, JSON.stringify(authenticatedProfile));
    // Tell the existing UI which identity has been verified by Supabase. This
    // prevents a name left in browser storage from impersonating a cloud user.
    window.dispatchEvent(new CustomEvent('guardian:cloud-session', { detail: authenticatedProfile }));
    try {
      const connections = await guardianCloud.listCalendarConnections();
      window.dispatchEvent(new CustomEvent('guardian:cloud-connections', { detail: connections }));
    } catch (error) {
      console.warn('Unable to load cloud calendar connections', error?.message || String(error), error?.status || '', error?.context || '');
      window.dispatchEvent(new CustomEvent('guardian:cloud-connections', { detail: [] }));
    }
    try {
      // A cloud account owns a full planning horizon, not a current-week
      // cache. Month, agenda, and schedule detection all use these records.
      const now = new Date();
      const rangeStart = new Date(now.getFullYear() - 1, 0, 1);
      const rangeEnd = new Date(now.getFullYear() + 2, 0, 1);
      const plannerItems = await guardianCloud.listItems(rangeStart.toISOString(), rangeEnd.toISOString());
      window.dispatchEvent(new CustomEvent('guardian:cloud-items', { detail: plannerItems }));
    } catch (error) {
      console.warn('Unable to load cloud planner items', error?.message || String(error));
    }
    const remotePreferences = await guardianCloud.loadPreferences();
    if (remotePreferences && !sessionStorage.getItem(CLOUD_PREFS_HYDRATED)) {
      sessionStorage.setItem(CLOUD_PREFS_HYDRATED, 'true');
      localStorage.setItem(PREFS_KEY, JSON.stringify(remotePreferences));
      location.reload();
      return;
    }
    const remote = await guardianCloud.loadSnapshot();
    // A secure profile is cloud-authoritative. Browser storage is only a
    // transient rendering cache, never a source to import into a cloud account.
    if (remotePreferences == null && !sessionStorage.getItem(CLOUD_PREFS_HYDRATED)) {
      await guardianCloud.savePreferences(CLOUD_DEFAULTS);
      sessionStorage.setItem(CLOUD_PREFS_HYDRATED, 'true');
      localStorage.setItem(PREFS_KEY, JSON.stringify(CLOUD_DEFAULTS));
      location.reload();
      return;
    }
    if (remote?.snapshot && !sessionStorage.getItem(CLOUD_SNAPSHOT_HYDRATED)) {
      sessionStorage.setItem(CLOUD_SNAPSHOT_HYDRATED, 'true');
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(remote.snapshot));
      location.reload();
      return;
    }
    if (!remote?.snapshot && !sessionStorage.getItem(CLOUD_SNAPSHOT_HYDRATED)) {
      sessionStorage.setItem(CLOUD_SNAPSHOT_HYDRATED, 'true');
      localStorage.removeItem(SNAPSHOT_KEY);
      window.CleanApp?.clearStarterContentForAccount?.();
      const emptySnapshot = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || 'null');
      if (emptySnapshot) await guardianCloud.saveSnapshot(emptySnapshot);
      location.reload();
    }
  }
  else {
    const saved = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (saved?.cloud) {
      // A cloud profile is never allowed to survive as a local-looking login.
      // Clear the stale shell and rerender as signed out.
      localStorage.removeItem(SESSION_KEY);
      location.reload();
      return;
    }
  }

  security.textContent = 'Protected cloud account. Your password is handled by the secure sign-in service and is never stored by Calendar Guardian.';

  let saveTimer;
  addEventListener('guardian:persist', event => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => guardianCloud.saveSnapshot(event.detail).catch(() => {}), 700);
  });
  let preferenceSaveTimer;
  addEventListener('guardian:preferences', event => {
    clearTimeout(preferenceSaveTimer);
    preferenceSaveTimer = setTimeout(async () => {
      try {
        await guardianCloud.savePreferences(event.detail);
        dispatchEvent(new CustomEvent('guardian:preferences-saved', { detail: { cloud: true } }));
      } catch (error) {
        dispatchEvent(new CustomEvent('guardian:preferences-saved', { detail: { cloud: false, error: error?.message || 'Could not save preferences' } }));
      }
    }, 350);
  });

  const resetButton = document.createElement('button');
  resetButton.type = 'button'; resetButton.className = 'secondary-action'; resetButton.textContent = 'Forgot password?';
  form.appendChild(resetButton);
  resetButton.addEventListener('click', async () => {
    const email = form.elements.email.value.trim();
    if (!email) return setMessage(form, 'Enter your email address first.', true);
    try { await guardianCloud.requestPasswordReset(email); setMessage(form, 'Password-reset email sent.'); }
    catch (error) { setMessage(form, error.message || 'Unable to send the reset email.', true); }
  });

  const googleButton = document.querySelector('.google-auth');
  googleButton?.addEventListener('click', async () => {
    googleButton.disabled = true;
    const original = googleButton.innerHTML;
    googleButton.textContent = 'Opening Google…';
    try {
      guardianCloud.setStaySignedIn(Boolean(form.elements.staySignedIn?.checked));
      location.assign(await guardianCloud.signInWithGoogle());
    } catch (error) {
      googleButton.disabled = false;
      googleButton.innerHTML = original;
      setMessage(form, error.message || 'Unable to start Google sign-in.', true);
    }
  });

  form.addEventListener('submit', async event => {
    event.preventDefault();
    event.stopImmediatePropagation();
    const create = !form.querySelector('.auth-name').hidden;
    const submit = form.querySelector('button[type="submit"]');
    submit.disabled = true;
    setMessage(form, create ? 'Creating your secure account…' : 'Signing in…');
    try {
      const values = new FormData(form);
      const email = String(values.get('email') || '').trim();
      const password = String(values.get('password') || '');
      const iconChoice = values.get('iconChoice') || 'female';
      guardianCloud.setStaySignedIn(Boolean(form.elements.staySignedIn?.checked));
      const result = create
        ? await guardianCloud.signUp({ email, password, name: String(values.get('name') || '').trim(), iconChoice })
        : await guardianCloud.signIn({ email, password });
      if (!result.user) throw new Error('Check your email to confirm the account, then sign in.');
      localStorage.setItem(SESSION_KEY, JSON.stringify(sessionRecord(result.user)));
      location.reload();
    } catch (error) {
      setMessage(form, error.message || 'Unable to sign in.', true);
      submit.disabled = false;
    }
  }, true);

  const signout = await waitFor('.auth-signout');
  signout.addEventListener('click', async event => {
    event.preventDefault();
    event.stopImmediatePropagation();
    await guardianCloud.signOut();
    localStorage.removeItem(SESSION_KEY);
    location.reload();
  }, true);

  const signedIn = signout.closest('.auth-signed-in');
  const deleteButton = document.createElement('button');
  deleteButton.type = 'button'; deleteButton.className = 'auth-signout'; deleteButton.textContent = 'Delete account';
  signedIn.appendChild(deleteButton);
  deleteButton.addEventListener('click', async () => {
    if (!confirm('Permanently delete your Calendar Guardian account and planner data? This cannot be undone.')) return;
    deleteButton.disabled = true;
    try { await guardianCloud.deleteAccount(); localStorage.clear(); sessionStorage.clear(); location.reload(); }
    catch (error) { deleteButton.disabled = false; alert(error.message || 'Account deletion failed.'); }
  });

  // Profile-image persistence belongs to the feature layer, which stores the
  // avatar path and crop alongside the secure profile before updating the UI.
  // Do not reload after a raw storage upload here: that used to interrupt the
  // save and leave the default icon selected.

  document.addEventListener('submit', async event => {
    const connectionForm = event.target.closest('.connection-form');
    if (!connectionForm) return;
    const provider = connectionForm.elements.provider.value;
    if (!['Google', 'Microsoft'].includes(provider)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    try { await guardianCloud.beginCalendarConnection(provider.toLowerCase()); }
    catch (error) { setMessage(connectionForm, error.message || 'Unable to begin connection.', true); }
  }, true);
}

initializeCloudAuth().catch(error => console.error('Cloud initialization failed', error?.message || String(error), error?.status || '', error?.context || ''));
